import * as d3 from 'd3'
import moment from 'moment'
import _ from 'underscore'

import { timeScaleWeighted } from '../../helper/d3.weightedTimeScale'
import dateFormatter from '../../helper/dateFormatter'
import {layersDownstreamLayers} from "../../domain/Layers";

//
// basis: http://bl.ocks.org/mstanaland/6100713
//
const Chart = function() {
    let svg = null
    let downstreamClipPath = 'downstreamClipPath'

    const dimensions = {
        width: 0,
        height: 0,
        gutter: 1,
    }

    const margin = {
        top: 15,
        right: 40,
        bottom: 30,
        left: 40,
    }

    const services = layersDownstreamLayers()

    let lines = null
    let scales = null
    let callbackTimestampSelected

    let handlersRegistered = false

    this.create = function(rootElement, width, height, options, onTimestampSelected, idSuffix) {
        if (idSuffix) {
            downstreamClipPath += idSuffix
        }

        svg = rootElement
        dimensions.width = width
        dimensions.height = height
        callbackTimestampSelected = onTimestampSelected

        const d3Svg = d3
            .select(svg)
            .attr('class', 'chart')
            .attr('width', dimensions.width)
            .attr('height', dimensions.height)
        const container = d3Svg.append('g').attr('class', 'container')

        const axis = container.append('g').attr('class', 'axis')

        axis.append('g').attr('class', 'x')
        const y = axis.append('g').attr('class', 'y')
        const y2 = axis.append('g').attr('class', 'y2')

        y.append('text')
            .attr('class', 'label')
            .attr('text-anchor', 'start')
            .attr('x', -40)
            .attr('y', -5)
            .text('Average Response Time')

        y2.append('text')
            .attr('class', 'label')
            .attr('text-anchor', 'start')
            .attr('x', -30)
            .attr('y', -5)
            .text('')

        const lines = container.append('g').attr('class', 'lines')

        lines
            .append('clipPath')
            .attr('id', downstreamClipPath)
            .append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)

        lines
            .append('rect')
            .attr('class', 'background')
            .attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top)

        lines
            .append('rect')
            .attr('class', 'highlight')
            .attr('style', { display: 'none' })

        container.append('g').attr('class', 'rangeMarkers')
    }

    const _registerEventHandlers = function(root) {
        const highlight = d3.select(svg).select('.highlight')

        d3.select(svg)
            .on('mouseover', function() {
                highlight.style('display', null)
            })
            .on('mouseleave', function() {
                highlight.style('display', 'none')
                callbackTimestampSelected(null, null, null)
            })

        root.on('mousemove', function() {
            const [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

            if (!dataSet) {
                return
            }

            const x = scales.x(dataSet.data.x)
            const width = scales.x(nextDataSet.data.x) - scales.x(dataSet.data.x)
            const y = d3.event.clientY

            callbackTimestampSelected(dataSet ? dataSet.data.dateUtc : null, nextDataSet ? nextDataSet.data.dateUtc : null, {
                x: x + margin.left,
                width,
                y,
            })

            highlight
                .attr('x', x)
                .attr('y', 0)
                .attr('width', width)
                .attr('height', dimensions.height - margin.top - margin.bottom)
        })

        const start = [null, null]
        const selection = [null, null]
        const dragBehavior = d3
            .drag()
            .on('start', function() {
                const [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (!dataSet) {
                    return
                }

                start[0] = selection[0] = dataSet.data
                start[1] = selection[1] = nextDataSet.data

                highlight
                    .attr('x', scales.x(selection[0].date))
                    .attr('y', 0)
                    .attr('width', scales.x(selection[1].date) - scales.x(selection[0].date))
                    .attr('height', dimensions.height - margin.top - margin.bottom)
                    .style('display', null)
            })
            .on('drag', function() {
                const [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (!dataSet) {
                    return
                }

                if (nextDataSet.data.x > start[0].x) {
                    selection[1] = nextDataSet.data
                }

                if (dataSet.data.x < start[1].x) {
                    selection[0] = dataSet.data
                }

                highlight
                    .attr('x', scales.x(selection[0].date))
                    .attr('y', 0)
                    .attr('width', scales.x(selection[1].date) - scales.x(selection[0].date))
                    .attr('height', dimensions.height - margin.top - margin.bottom)
                    .style('display', null)
            })
            .on('end', function() {
                const [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (!dataSet) {
                    return
                }

                if (nextDataSet.data.date > start[0].date) {
                    selection[1] = nextDataSet.data
                }

                if (dataSet.data.date < start[1].date) {
                    selection[0] = dataSet.data
                }

                jQuery(svg).trigger('rangeSelected', selection)
            })

        root.call(dragBehavior)
    }

    this.highlight = function(timeIndex, timeIndexNext) {
        const highlight = d3.select(svg).select('.highlight')

        highlight.style('display', 'none')

        if (!timeIndex || !timeIndexNext || !scales) {
            return
        }

        highlight
            .attr('x', scales.x(timeIndex))
            .attr('y', 0)
            .attr('width', scales.x(timeIndexNext) - scales.x(timeIndex))
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .style('display', null)
    }

    const _getSelectedItem = function(mouse) {
        const bisect = d3.bisector(function(d) {
            return d.data.x
        }).right

        const x0 = scales.x.invert(mouse[0])
        const dateItem = Math.min(bisect(lines[0], x0), lines[0].length - 1)
        const date = lines[0][dateItem - 1]
        const nextDate = lines[0][dateItem]

        return [date, nextDate]
    }

    const _scales = function(lines) {
        return {
            x: d3
                .scaleTime()
                .domain([d3.min(lines[0], d => d.data.x), d3.max(lines[0], d => d.data.x)])
                .range([0, dimensions.width - margin.left - margin.right]),
            y: d3
                .scaleLinear()
                .domain([d3.max(lines, d => d3.max(d, d => d[1])), 0])
                .range([0, dimensions.height - margin.top - margin.bottom]),
        }
    }

    const _axis = function(scales) {
        return {
            x: d3
                .axisBottom(scales.x)
                .tickSizeInner(-(dimensions.height - margin.top - margin.bottom))
                .tickSizeOuter(3)
                .tickPadding(5)
                .ticks(10)
                .tickFormat(dateFormatter),
            y: d3
                .axisLeft(scales.y)
                .tickSizeInner(-(dimensions.width - margin.left - margin.right))
                .tickSizeOuter(3)
                .tickPadding(5)
                .ticks(2),
            y2: d3.axisRight(scales.y).tickFormat(''),
            /*.tickSizeInner(-(dimensions.width - margin.left - margin.right))
                .tickSizeOuter(3)
                .tickPadding(5),
                .ticks(2),*/
        }
    }

    const _drawAxis = function(scales, axis) {
        const root = d3.select(svg).select('.axis')
        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        root.selectAll('.x')
            .call(axis.x)
            .attr('transform', 'translate(0, ' + (dimensions.height - margin.bottom - margin.top) + ')')

        root.selectAll('.y').call(axis.y)

        root.selectAll('.y2')
            .call(axis.y2)
            .attr('transform', 'translate(' + (dimensions.width - margin.left - margin.right) + ', 0)')

        d3.select(svg)
            .selectAll('.axis .x .tick text')
            .attr('class', d => timeScaleWeighted(d, axis.x.scale().domain()))
    }

    const _drawData = function(scales, lines) {
        const root = d3.select(svg).select('.lines')

        if (lines[0].length < 2) {
            return
        }

        const firstDate = lines[0][0].data.x
        const secondDate = lines[0][1].data.x
        const tickWidth = scales.x(secondDate) - scales.x(firstDate) - dimensions.gutter

        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        let group = root.selectAll('g.line').data(lines)
        const newGroup = group
            .enter()
            .append('g')
            .attr('clip-path', `url(#${downstreamClipPath})`)
            .attr('class', (lines, index) => 'line layer-' + services[index])

        group = newGroup.merge(group)

        const rect = group.selectAll('rect').data(d => d)

        rect.enter()
            .append('rect')
            .attr('x', d => scales.x(d.data.x))
            .attr('y', d => scales.y(d[1]))
            .attr('height', d => d3.max([scales.y(d[0]) - scales.y(d[1]), 0]))
            .attr('width', tickWidth)

        group
            .selectAll('rect')
            .attr('x', d => scales.x(d.data.x))
            .attr('y', d => scales.y(d[1]))
            .attr('height', d => d3.max([scales.y(d[0]) - scales.y(d[1]), 0]))
            .attr('width', tickWidth)
            .transition()
            .attr('transition', 'translate(' + scales.x(-1) + ')')

        // Exiting node
        group.exit().remove()
        rect.exit().remove()
    }

    const _drawRangeMarkers = function(scales, rangeMarkers) {
        const root = d3.select(svg).select('.rangeMarkers')
        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        const marker = root.selectAll('g.rangeMarker').data(rangeMarkers)

        // New data
        const newevent = marker.enter()
        let group = newevent.append('g').attr('class', function(marker) {
            return 'rangeMarker ' + marker.type
        })

        group
            .append('rect')
            .attr('class', 'bg')
            .attr('y', 0)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .on('mousemove', function() {
                const [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (!dataSet) {
                    return
                }

                const x = scales.x(dataSet.data.x)
                const width = scales.x(nextDataSet.data.x) - scales.x(dataSet.data.x)
                const y = d3.event.clientY

                callbackTimestampSelected(dataSet ? dataSet.data.dateUtc : null, nextDataSet ? nextDataSet.data.dateUtc : null, {
                    x: x + margin.left,
                    width,
                    y,
                })

                d3.select(svg)
                    .select('.highlight')
                    .attr('x', x)
                    .attr('y', 0)
                    .attr('width', width)
                    .attr('height', dimensions.height - margin.top - margin.bottom)
                    .style('display', null)
            })

        const button = group
            .append('g')
            .attr('class', 'button')
            .attr('width', 17)
            .attr('height', 17)

        button.on('click', function() {
            d3.event.stopPropagation()
            jQuery(svg).trigger('rangeDelesect')
        })

        button
            .append('rect')
            .attr('class', 'shadow')
            .attr('width', 16)
            .attr('height', 16)
            .attr('x', 1)
            .attr('y', 1)

        button
            .append('rect')
            .attr('class', 'bg')
            .attr('width', 16)
            .attr('height', 16)
            .attr('x', 0)
            .attr('y', 0)

        button
            .append('text')
            .attr('class', 'button icon')
            .attr('x', 3)
            .attr('y', 13)
            .text('\uf00d')

        group
            .append('path')
            .attr('class', 'marker')
            .attr('d', function(marker) {
                const position = scales.x(marker.start + (marker.end - marker.start) / 2)

                return 'M ' + position + ' 4 L ' + (position + 3) + ' 0 L ' + (position - 3) + ' 0 Z'
            })

        group = group.merge(marker)

        // Update existing nodes
        group
            .select('rect.bg')
            .transition()
            .attr('x', function(marker) {
                return scales.x(marker.start)
            })
            .attr('width', function(marker) {
                return scales.x(marker.end) - scales.x(marker.start)
            })

        group
            .select('g.button')
            .transition()
            .attr('transform', function(marker) {
                return 'translate(' + (scales.x(marker.end) - 16 - 4) + ', 4)'
            })

        group
            .select('path.marker')
            .transition()
            .attr('d', function(marker) {
                const position = scales.x(marker.start + (marker.end - marker.start) / 2)

                return 'M ' + position + ' -1 L ' + (position + 4) + ' -5 L ' + (position - 4) + ' -5 Z'
            })

        // Exiting node
        marker.exit().remove()
    }

    const appendValue = function(dataSet) {
        if (dataSet.length < 2) {
            return dataSet
        }

        const previous = dataSet[dataSet.length - 2]
        const current = dataSet[dataSet.length - 1]
        const next = _.clone(current)
        const difference = moment.duration(moment(current.date).diff(moment(previous.date)))

        next.date = moment(next.date)
            .add(difference)
            .toDate()
        next.averageResponse = 0 // Not necessary, but the chart closes in a more sensible way

        dataSet[dataSet.length] = next

        return dataSet
    }

    // performance view has always ~60 + 1 entries, keep the gutter there
    const gutterSize = data => (data.length > 65 ? 0 : 1)

    this.update = function(data, rangeMarkers) {
        lines = appendValue(data)
        lines = lines.map(d => {
            const item = {
                x: d.date,
                y: 0,
                percent: 0,
                averageResponse: d.averageResponse,
                dateUtc: d.dateUtc,
                date: d.date, // duplicate 'x' for compat reasons (drag'n'drop)
            }

            services.map(service => {
                item['downstream.' + service] = 0
            })

            if (!d.downstream) {
                return item
            }

            let remainingResponseTime = item.averageResponse

            services.forEach(service => {
                const downstream = d.downstream[service] ?? null
                if (!downstream) {
                    return
                }

                const percentage = downstream.requests / d.requests
                const normalizedAverage = downstream.average * percentage

                remainingResponseTime -= normalizedAverage

                item['downstream.' + service] = normalizedAverage
            })

            item['downstream.remainder'] = remainingResponseTime

            return item
        })

        const stack = d3
            .stack()
            .keys(services.map(service => 'downstream.' + service))
            .order(d3.stackOrderNone)
            .offset(d3.stackOffsetNone)

        lines = stack(lines)

        dimensions.gutter = gutterSize(data)

        scales = _scales(lines)

        _drawAxis(scales, _axis(scales))
        _drawData(scales, lines)
        _drawRangeMarkers(scales, rangeMarkers)

        if (!handlersRegistered) {
            handlersRegistered = true
            _registerEventHandlers(d3.select(svg).select('.lines'))
        }
    }

    this.destroy = function() {
        // remove all children from the svg.
        d3.select(svg)
            .selectAll('*')
            .remove()
    }
}

export default Chart
